import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Lock, LockOpen, CalendarRange } from 'lucide-react';
import { type PeriodDto, type PeriodSummaryDto } from '@workforce/shared';
import { formatMinutes, periodName, t } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useTz } from '@/lib/timezone';
import { usePeriods } from '@/lib/periods';
import { fmtDate } from '@/lib/format';
import { Badge, Button, Card, ConfirmDialog, EmptyState, ErrorBanner, Field, Input, Notice, PageHeader, Select, Spinner, Textarea } from '@/components/ui';
import { ExportMenu } from '@/components/ExportMenu';
import { Diff, TotalsBar } from '@/components/AttendanceTable';
import { pctTone } from '@/components/ProductivityParts';

export function PeriodBadge({ p }: { p: PeriodDto }) {
  if (p.status === 'closed') return <Badge tone="slate">{t('Closed')}</Badge>;
  if (p.isCurrent) return <Badge tone="brand">{t('Current')}</Badge>;
  if (p.hasEnded) return <Badge tone="amber">{t('Ended — not closed')}</Badge>;
  return <Badge tone="slate">{t('Upcoming')}</Badge>;
}

export function periodRange(p: Pick<PeriodDto, 'startDate' | 'endDate'>) {
  return t('{from} → {to}', { from: fmtDate(p.startDate, 'd LLL'), to: fmtDate(p.endDate, 'd LLL yyyy') });
}

export function ReportsPage() {
  const { can, user } = useAuth();
  const target = user?.productivityTargetPct ?? 70;
  const { fmt } = useTz();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const list = usePeriods();
  const visible = useMemo(() => (list.data ?? []).filter((p) => p.hasStarted), [list.data]);
  // Default: the oldest ended period that is still open (needs attention), else the current one.
  const fallback = useMemo(() => {
    const waiting = [...visible].reverse().find((p) => p.hasEnded && p.status === 'open');
    return (waiting ?? visible.find((p) => p.isCurrent) ?? visible[0])?.id;
  }, [visible]);
  // Custom dates: ?from=YYYY-MM-DD&to=YYYY-MM-DD (any range up to a year).
  const rangeFrom = params.get('from');
  const rangeTo = params.get('to');
  const isRange = !!(rangeFrom && rangeTo);
  const periodId = isRange ? null : params.get('period') ?? fallback;
  useEffect(() => {
    if (!isRange && !params.get('period') && fallback) setParams({ period: fallback }, { replace: true });
  }, [fallback, params, setParams, isRange]);
  const [draftRange, setDraftRange] = useState({ from: rangeFrom ?? '', to: rangeTo ?? '' });

  const q = useQuery({
    queryKey: ['periods', 'summary', periodId ?? `${rangeFrom}_${rangeTo}`],
    queryFn: () =>
      api.get<PeriodSummaryDto>(isRange ? `/periods/range/summary?from=${rangeFrom}&to=${rangeTo}` : `/periods/${periodId}/summary`),
    enabled: isRange || !!periodId,
  });
  const [dialog, setDialog] = useState<'close' | 'reopen' | null>(null);
  const [note, setNote] = useState('');
  const refresh = () => {
    setDialog(null);
    qc.invalidateQueries({ queryKey: ['periods'] });
    qc.invalidateQueries({ queryKey: ['attendance'] });
    qc.invalidateQueries({ queryKey: ['payroll'] });
  };
  const close = useMutation({ mutationFn: () => api.post(`/periods/${periodId}/close`, { note: note || undefined }), onSuccess: refresh });
  const reopen = useMutation({ mutationFn: (reason: string) => api.post(`/periods/${periodId}/reopen`, { reason }), onSuccess: refresh });

  if (list.isLoading) return <Spinner />;
  const s = q.data;
  const p = s?.period ?? visible.find((x) => x.id === periodId);
  const canManage = can('periods.manage') && !isRange;
  const checks = s?.checks;
  const warnings = checks
    ? [
        checks.notFinished.length
          ? t(checks.notFinished.length === 1 ? 'Not finished yet for {n} person (their last day is not over).' : 'Not finished yet for {n} people (their last day is not over).', { n: checks.notFinished.length })
          : null,
        checks.missingPunchDays
          ? t('{n} day(s) with a missing clock-in/out ({names}).', {
              n: checks.missingPunchDays,
              names: `${checks.employeesWithMissingPunches.slice(0, 4).join(', ')}${checks.employeesWithMissingPunches.length > 4 ? '…' : ''}`,
            })
          : null,
        checks.pendingLeaveRequests ? t('{n} leave request(s) in this period still waiting for approval.', { n: checks.pendingLeaveRequests }) : null,
      ].filter(Boolean) as string[]
    : [];

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('Reports')}
        subtitle={t('Month-end summary per reporting period. Closing a period freezes its attendance so the figures used for payroll never change afterwards.')}
        actions={(isRange || periodId) && <ExportMenu kind="period-report" params={isRange ? { from: rangeFrom, to: rangeTo } : { period: periodId }} />}
      />
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('Report for')} className="min-w-72">
          <Select
            value={isRange ? 'custom' : periodId ?? ''}
            onChange={(e) => {
              if (e.target.value === 'custom') {
                const cur = visible.find((x) => x.id === periodId);
                const from = draftRange.from || cur?.startDate || '';
                const to = draftRange.to || cur?.endDate || '';
                setDraftRange({ from, to });
                if (from && to) setParams({ from, to });
              } else setParams({ period: e.target.value });
            }}
          >
            {visible.map((x) => (
              <option key={x.id} value={x.id}>
                {periodName(x.label)} · {periodRange(x)}
                {x.status === 'closed' ? ` · ${t('closed')}` : x.isCurrent ? ` · ${t('current')}` : x.hasEnded ? ` · ${t('to close')}` : ''}
              </option>
            ))}
            <option value="custom">{t('Custom dates…')}</option>
          </Select>
        </Field>
        {isRange && (
          <>
            <Field label={t('From')}>
              <Input type="date" value={draftRange.from} onChange={(e) => setDraftRange((r) => ({ ...r, from: e.target.value }))} />
            </Field>
            <Field label={t('To')}>
              <Input type="date" value={draftRange.to} onChange={(e) => setDraftRange((r) => ({ ...r, to: e.target.value }))} />
            </Field>
            <Button
              disabled={!draftRange.from || !draftRange.to || (draftRange.from === rangeFrom && draftRange.to === rangeTo)}
              onClick={() => setParams({ from: draftRange.from, to: draftRange.to })}
            >
              {t('Show')}
            </Button>
          </>
        )}
      </div>

      {p && (
        <Card>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="rounded-lg bg-brand-50 p-2 text-brand-700">
                <CalendarRange className="h-5 w-5" />
              </span>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-slate-900">{periodName(p.label)}</h2>
                  {isRange ? <Badge tone="brand">{t('Custom dates')}</Badge> : <PeriodBadge p={p} />}
                </div>
                <p className="text-sm text-slate-500">{periodRange(p)}</p>
                {p.status === 'closed' && (
                  <p className="mt-1 text-xs text-slate-500">
                    {t('Closed by {name} on {date}', { name: p.closedBy ?? t('system'), date: fmt(p.closedAt, null, 'dd LLL yyyy, HH:mm') })}
                    {p.closeVersion > 1 ? ` · ${t('version {n} (reopened before)', { n: p.closeVersion })}` : ''}
                  </p>
                )}
              </div>
            </div>
            {canManage && (
              <div className="flex gap-2">
                {p.status === 'open' && p.hasEnded && (
                  <Button icon={<Lock className="h-4 w-4" />} onClick={() => { setNote(''); setDialog('close'); }}>
                    {t('Close period')}
                  </Button>
                )}
                {p.status === 'closed' && (
                  <Button variant="secondary" icon={<LockOpen className="h-4 w-4" />} onClick={() => setDialog('reopen')}>
                    {t('Reopen')}
                  </Button>
                )}
              </div>
            )}
          </div>
          {!isRange && p.status === 'open' && p.hasEnded && s && (
            <div className="mt-4">
              {warnings.length ? (
                <Notice tone="amber">
                  <p className="mb-1 flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" /> {t('Check before closing')}</p>
                  <ul className="list-disc ps-5">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
                </Notice>
              ) : (
                <Notice tone="green">
                  <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> {t('No missing punches or pending leave — ready to close.')}</span>
                </Notice>
              )}
            </div>
          )}
          {p.isCurrent && <p className="mt-3 text-xs text-slate-500">{t('Figures so far — the period is still running.')}</p>}
        </Card>
      )}

      <ErrorBanner error={q.error} />
      {q.isLoading && <Spinner />}
      {s && (
        <>
          <TotalsBar totals={s.totals} />
          <Card bodyClass="p-0" title={s.source === 'closed' ? t('Frozen figures (as closed)') : t('Live figures')}>
            {s.rows.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-start text-xs font-medium uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-2">{t('Employee')}</th>
                      <th className="hidden px-3 py-2 lg:table-cell">{t('Department')}</th>
                      <th className="px-3 py-2 text-end">{t('Worked')}</th>
                      <th className="hidden px-3 py-2 text-end sm:table-cell">{t('Expected')}</th>
                      <th className="px-3 py-2 text-end">+/−</th>
                      {s.rows.some((r) => r.productivity) && <th className="hidden px-3 py-2 text-end md:table-cell" title={t('Computer activity: productive ÷ tracked')}>{t('Score')}</th>}
                      <th className="hidden px-3 py-2 text-end md:table-cell">{t('Present')}</th>
                      <th className="hidden px-3 py-2 text-end md:table-cell">{t('Late')}</th>
                      <th className="hidden px-3 py-2 text-end md:table-cell">{t('Early')}</th>
                      <th className="px-3 py-2 text-end">{t('Absent')}</th>
                      <th className="hidden px-3 py-2 text-end sm:table-cell">{t('Missing')}</th>
                      <th className="hidden px-3 py-2 lg:table-cell">{t('Leave')}</th>
                      <th className="hidden px-3 py-2 text-end xl:table-cell">{t('Holidays')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.rows.map((r) => (
                      <tr key={r.employeeId} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="whitespace-nowrap px-3 py-2">
                          <Link
                            to={`/attendance?tab=employee&employee=${r.employeeId}&from=${s.period.startDate}&to=${s.period.endDate}`}
                            className="font-medium text-brand-700 hover:underline"
                          >
                            {r.employeeName}
                          </Link>
                          <span className="ms-1.5 text-xs text-slate-400">{r.employeeCode}</span>
                        </td>
                        <td className="hidden px-3 py-2 text-slate-600 lg:table-cell">{r.departmentName ?? '—'}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-end font-medium tabular-nums">{formatMinutes(r.attendance.workedMinutes)}</td>
                        <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums text-slate-500 sm:table-cell">{formatMinutes(r.attendance.scheduledMinutes)}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-end tabular-nums"><Diff minutes={r.attendance.differenceMinutes} /></td>
                        {s.rows.some((x) => x.productivity) && (
                          <td className={`hidden whitespace-nowrap px-3 py-2 text-end font-semibold tabular-nums md:table-cell ${pctTone(r.productivity?.productivityPct ?? null, target)}`} title={r.productivity ? t('{productive} productive of {tracked} tracked', { productive: formatMinutes(r.productivity.productiveMinutes), tracked: formatMinutes(r.productivity.trackedMinutes) }) : undefined}>
                            {r.productivity?.productivityPct == null ? '—' : `${r.productivity.productivityPct}%`}
                          </td>
                        )}
                        <td className="hidden px-3 py-2 text-end tabular-nums md:table-cell">{r.attendance.presentDays}/{r.workdays}</td>
                        <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums md:table-cell">
                          {r.attendance.lateDays ? <span className="text-amber-700">{r.attendance.lateDays} · {formatMinutes(r.attendance.lateMinutes)}</span> : '—'}
                        </td>
                        <td className="hidden px-3 py-2 text-end tabular-nums md:table-cell">{r.attendance.earlyLeaveDays || '—'}</td>
                        <td className="px-3 py-2 text-end tabular-nums">{r.attendance.absentDays ? <span className="text-red-600">{r.attendance.absentDays}</span> : '—'}</td>
                        <td className="hidden px-3 py-2 text-end tabular-nums sm:table-cell">{r.attendance.missingPunchDays ? <span className="text-amber-700">{r.attendance.missingPunchDays}</span> : '—'}</td>
                        <td className="hidden px-3 py-2 lg:table-cell">
                          {r.leave.length ? (
                            <span className="flex flex-wrap gap-x-2 gap-y-0.5 whitespace-nowrap text-xs">
                              {r.leave.map((l) => (
                                <span key={l.leaveTypeId} className="inline-flex items-center gap-1">
                                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: l.color }} />
                                  {l.name} {l.days}
                                </span>
                              ))}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="hidden px-3 py-2 text-end tabular-nums xl:table-cell">{r.holidays || '—'}</td>
                                            </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState title={t('Nobody to show for this period')} />
            )}
          </Card>
          <p className="text-xs text-slate-500">
            {t('Click a name for the day-by-day detail. Score = productive ÷ tracked computer time (desktop agent). Excel, CSV and PDF exports are added in Phase 8.')}
          </p>
        </>
      )}

      <ConfirmDialog
        open={dialog === 'close'}
        onClose={() => setDialog(null)}
        onConfirm={() => close.mutate()}
        title={t('Close {period}?', { period: p ? periodName(p.label) : '' })}
        message={
          <>
            <p>{t('Attendance for {dates} is frozen and a summary is stored for every employee. Corrections and leave changes on these dates are then refused until an administrator reopens the period (with a reason).', { dates: p ? periodRange(p) : '' })}</p>
            {warnings.length > 0 && (
              <ul className="mt-3 list-disc ps-5 text-amber-800">
                {warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            )}
          </>
        }
        confirmLabel={t('Close period')}
        loading={close.isPending}
        error={close.error}
      >
        <Field label={t('Note (optional)')}>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder={t('e.g. Sent to payroll on 26 Sep')} />
        </Field>
      </ConfirmDialog>
      <ConfirmDialog
        open={dialog === 'reopen'}
        onClose={() => setDialog(null)}
        onConfirm={(reason) => reopen.mutate(reason)}
        title={t('Reopen {period}?', { period: p ? periodName(p.label) : '' })}
        message={t('Attendance for this period becomes editable again. The summary stored at closing is kept as an earlier version; closing again stores a new one.')}
        confirmLabel={t('Reopen period')}
        danger
        requireReason
        loading={reopen.isPending}
        error={reopen.error}
      />
    </div>
  );
}
